import { useStateMachine } from "little-state-machine";
import Steps from "../../Components/Steps";
import FormNavigation from "../FormNavigation";
import Axios, { AxiosRequestConfig } from "axios";
import { ErrorOrLoading } from "../../../shared";
import { UploadType } from "../../Components/DropZone";
import { BuinessHoursType } from "../../Components/BusinessHours";
import { motion } from "framer-motion";
import { useTradeWasteContext } from "../tradeWasteContext";
import { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";

interface Step5Interface {
  endpoint: string
}

const Step5 = (props: Step5Interface) => {
  const [loading, setLoading] = useState(false)
  const [postError, setPostError] = useState<Error>()
  const { state: { data } } = useStateMachine();
  const [context] = useTradeWasteContext();
  const navigate = useNavigate()
  useEffect(() => {
    window?.scrollTo(0, 0)
  }, [])

  const submitData = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true);
    const modifiedData = {
      ...data,
      operator_number: (data?.operator_number_area_code ?? "+61") + (data?.operator_number ?? ""),
      declaration_phone: (data?.declaration_phone_area_code ?? "+61") + (data?.declaration_phone ?? "")
    }
    const modifiedUploads = {
      menu: context?.menus,
      plumbing_plan: context?.plumbingPlans,
    }
    const options: AxiosRequestConfig = {
      method: "POST",
      headers: { "content-type": "application/json" },
      data: { data: modifiedData, uploads: modifiedUploads },
      url: props.endpoint,
    };
    try {
      await Axios(options);
      setLoading(false);
      (window as any)?.dataLayer && (window as any).dataLayer.push({ 'event': 'formSubmission' });
      navigate("/receipt");
    } catch (error) {
      if ((window as any)?.digitalData && (window as any)?.digitalData?.form && (window as any)?.dataLayer) {
        (window as any).digitalData.form.errorList = error;//e.g. How do you currently receive…
        (window as any).digitalData.form.name = "Trade waste from";
        (window as any).dataLayer.push({ event: 'formErrorBlocking' });
      }
      setLoading(false);
      // @ts-ignore
      if (error?.response) {
        // Request made and server responded
        // @ts-ignore
        setPostError(error?.response?.data ? error?.response?.data : "Please try again")
        // @ts-ignore
      } else if (error?.request) {
        // The request was made but no response was received
        setPostError(new Error("network error, please try again"))
      } else {
        // Something happened in setting up the request that triggered an Error
        setPostError(new Error("something went wrong, please try again"))
      }
    }
  }
  const onBack = () => {
    navigate("/step4");
  };

  return (
    <motion.div
      data-testid="step5"
      key="step5"
      initial="initial"
      animate="in"
      exit="out"
      variants={{
        initial: {
          opacity: 0,
        },
        in: {
          opacity: 1,
        },
        out: {
          opacity: 0,
        },
      }}
    >
      <Steps step={5} max={5} />
      <form onSubmit={submitData}>
        {
          (loading || postError) && (
            <div className="relative" style={{ height: "500px", width: "100%" }}>
              <ErrorOrLoading loading={loading} error={postError} />
            </div>
          )
        }
        {!loading && (
          <div>
            <h2>Application preview</h2>
            <p>
              Please review your application, if any details are incorrect please go
              back and correct them prior to submitting your application.
            </p>
            <div className="wc-msg wc-msg--grey wc-concession-form">
              <section>
                <h3>Application type</h3>
                <p>Food preparation</p>
              </section>
              <hr />
              <section>
                <h3>Business details</h3>
                <h4>Site details</h4>
                <dl>
                  <dt>Unit/shop number</dt>
                  <dd>{data?.unit_shop_number}</dd>
                  <dt>Street number</dt>
                  <dd>{data?.street_number}</dd>
                  <dt>Street name</dt>
                  <dd>{data?.street_name}</dd>
                  <dt>Nearest intersection</dt>
                  <dd>{data?.nearest_intersection}</dd>
                  <dt>Suburb</dt>
                  <dd>{data?.suburb}</dd>
                  <dt>Postcode</dt>
                  <dd>{data?.postcode}</dd>
                  <dt>Building / Shopping centre name</dt>
                  <dd>{data?.building_shopping_centre_name}</dd>
                </dl>
              </section>
              <hr />
              <section>
                <h4>Business details</h4>
                <dl>
                  <dt>Business trading name</dt>
                  <dd>{data?.business_trading_name}</dd>
                  <dt>ABN</dt>
                  <dd>{data?.abn}</dd>
                  <dt>Company name</dt>
                  <dd>{data?.company_name}</dd>
                  <dt>Date of discharge commencement</dt>
                  <dd>{data?.date_of_discharge ? new Date(data.date_of_discharge).toString() : ""}</dd>
                  <dt>Is your business mailing address same as property address?</dt>
                  <dd>{data?.mailing_same}</dd>
                  {data?.mailing_same === "No" && (
                    <>
                      <dt>Mailing address</dt>
                      <dd>{data?.mailing_address}</dd>
                      <dt>Suburb</dt>
                      <dd>{data?.mailing_suburb}</dd>
                      <dt>Postcode</dt>
                      <dd>{data?.mailing_postcode}</dd>
                    </>
                  )}
                </dl>
                <hr />
                <h4>Business owner/operator's details</h4>
                <dl>
                  <dt>Name</dt>
                  <dd>{data?.operator_name}</dd>

                  <dt>Position</dt>
                  <dd>{data?.operator_position}</dd>

                  <dt>Phone or mobile number</dt>
                  <dd>{data?.operator_number_area_code ?? "+61"}{data?.operator_number}</dd>

                  <dt>Email</dt>
                  <dd>{data?.operator_email}</dd>
                </dl>
                <hr />
                <h4>Business operating hours</h4>
                <ul className="list-none">
                  {data?.business_hours && data?.business_hours?.map((bh: BuinessHoursType, i: number) => (
                    <li className="mb-4" key={i}>
                      <dl className="flex w-100 justify-between">
                        <div>
                          <dt>Day</dt>
                          <dd>{bh?.day}</dd>
                        </div>
                        <div>
                          <dt>From</dt>
                          <dd>{bh?.from}</dd>
                        </div>
                        <div>
                          <dt>To</dt>
                          <dd>{bh?.to}</dd>
                        </div>
                      </dl>
                    </li>
                  ))}
                </ul>
              </section>
              <hr />
              <section>
                <h3>Process Details</h3>
                <h4>Details</h4>
                <dl>
                  <dt>
                    What will be the seating capacity of your business (including
                    alfresco)?
                  </dt>
                  <dd>{data?.seating_capacity}</dd>
                  <dt>
                    Will you be cooking food on premises?
                  </dt>
                  <dd>{data?.cooking_on_premises}</dd>
                  <dt>Kitchen sink</dt>
                  <dd>{data?.kitchen_sink}</dd>

                  <dt>Double kitchen sink</dt>
                  <dd>{data?.double_kitchen_sink}</dd>

                  <dt>Pot sink</dt>
                  <dd>{data?.pot_sink}</dd>

                  <dt>Double pot sink</dt>
                  <dd>{data?.double_pot_sink}</dd>

                  <dt>Hand basin</dt>
                  <dd>{data?.hand_basin}</dd>

                  <dt>Wok burners</dt>
                  <dd>{data?.wok_burners}</dd>

                  <dt>Combi ovens</dt>
                  <dd>{data?.combi_ovens}</dd>

                  <dt>Are you connecting to an existing grease arrestor?</dt>
                  <dd>
                    {data?.connecting_existing_grease_arrestor ?? "No"}
                  </dd>
                  {
                    data?.connecting_existing_grease_arrestor === "Yes" && (
                      <>
                        <dt>If you know, please enter existing Water Corporation barcode number for the grease arrestor</dt>
                        <dd>{data?.existing_arrestor_barcode}</dd>
                        <dt>Location of grease arrestor </dt>
                        <dd>{data?.existing_arrestor_location}</dd>
                        <dt>Size (in litres)</dt>
                        <dd>{data?.existing_arrestor_size}</dd>
                        <dt>Is the grease arrestor shared with any other businesses? </dt>
                        <dd>{data?.existing_arrestor_shared}</dd>

                        {data?.existing_arrestor_shared === "Yes" && (<h4>Contacts</h4>)}

                        {
                          data?.contacts?.map((c: { name: string; relationship: string; number: string }, i: number) => (
                            <dl className="flex w-100 justify-between" key={i}>
                              <div>
                                <dt>Name</dt>
                                <dd>{c?.name}</dd>
                              </div>
                              <div>
                                <dt>Relationship</dt>
                                <dd>{c?.relationship}</dd>
                              </div>
                              <div>
                                <dt>Number</dt>
                                <dd>{c?.number}</dd>
                              </div>
                            </dl>
                          ))
                        }
                      </>
                    )
                  }
                  <dt>Will you be installing a new grease arrestor?</dt>
                  <dd>
                    {data?.connecting_new_grease_arrestor ?? "No"}
                  </dd>
                  {
                    data?.connecting_new_grease_arrestor && data?.connecting_new_grease_arrestor === "Yes" && (
                      <>
                        <dt>Size (in litres)</dt>
                        <dd>{data?.new_arrestor_size}</dd>
                        <dt>Make/model</dt>
                        <dd>{data?.new_arrestor_make_model}</dd>
                        <dt>Planned installation date</dt>
                        <dd>{data?.new_arrestor_install_date ? new Date(data.new_arrestor_install_date).toString() : ""}</dd>
                        <dt>Name of licensed plumber</dt>
                        <dd>{data?.new_arrestor_licensed_plumber_name}</dd>
                        <dt>Contact number of the licensed plumber</dt>
                        <dd>{data?.new_arrestor_licensed_plumber_contact}</dd>
                      </>
                    )
                  }
                  <dt>Are you doing any fitouts or additional plumbing work?</dt>
                  <dd>
                    {data?.fitouts_additional_plumbing}
                  </dd>

                  <dt>Please attach your menu or proposed menu</dt>
                  {context?.menus?.map((m: UploadType, i: number) => (
                    <dd key={i}>{m?.name}</dd>
                  ))}

                  <dt>Please attach a plumbing plan</dt>
                  {context?.plumbingPlans?.map((m: UploadType, i: number) => (
                    <dd key={i}>{m?.name}</dd>
                  ))}
                </dl>
              </section>
              <hr />
              <section>
                <h3>Declaration</h3>
                <h4>Declaration by the applicant</h4>
                <dl>
                  <dt>Name</dt>
                  <dd>{data?.declaration_name}</dd>

                  <dt>Position</dt>
                  <dd>{data?.declaration_position}</dd>
                  <dt>Company name</dt>
                  <dd>{data?.declaration_company_name}</dd>

                  <dt>Phone or mobile number</dt>
                  <dd>{data?.declaration_phone_area_code ?? "+61"}{data?.declaration_phone}</dd>

                  <dt>Email</dt>
                  <dd>{data?.declaration_email}</dd>
                  <dt>
                    I confirm the information provided is true and correct and agree
                    with the above Terms and Conditions
                  </dt>
                  <dd>Accepted by {data?.declaration_name}</dd>
                </dl>
              </section>
            </div>
          </div>
        )
        }
        <FormNavigation isLast={true} handleBack={onBack} isDisabled={loading} />
      </form >
    </motion.div>
  );
};

export default Step5;
